import { useEffect, useRef } from "react";
import { useAnalyticsContext, TrackingEvent, ProductType } from "@/analytics";

/**
 * Hotel listing analytics hook
 *
 * Sorumluluklar:
 * - Otel listeleme sayfalarında VIEW_ITEM_LIST (ve ileride filtre/sort vb.) event'lerini tetiklemek
 * - Card komponentlerini analytics'ten bağımsız tutmak (sadece parent seviyede kullanılır)
 *
 * @param {Object} params
 * @param {Array<Object>} params.products       - Ekranda gösterilen otel listesi (slice)
 * @param {Object} params.passengers           - Misafir bilgileri { guest_total, guest_adult_number, guest_children_number }
 * @param {string} params.currencyCode         - Para birimi (örn: "TRY")
 * @param {string} [params.itemListId]         - GA4 item_list_id (örn: "hotel_search")
 * @param {string} [params.itemListName]       - GA4 item_list_name (örn: "Hotel Search")
 * @param {number} [params.startIndex]         - Global index offset (örn: ilk sayfada 1, ikinci batch'te 9 vb.)
 * @param {string} [params.crossProductPromotion] - Çapraz ürün kampanyası (örn: "flight_to_hotel")
 * @returns {{ resetViewedHotels: () => void }}
 */
export function useHotelAnalytics({
  products = [],
  passengers,
  currencyCode,
  itemListId = "hotel_search",
  itemListName = "Hotel Search",
  startIndex = 1,
  crossProductPromotion,
  enabled = true,
}) {
  const { dispatch: trackingDispatch } = useAnalyticsContext();

  // Daha önce kaç ürün için VIEW_ITEM_LIST gönderildiğini takip et
  const lastCountRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    if (!products || products.length === 0) return;

    const totalCount = products.length;
    const previouslySent = lastCountRef.current;

    // Yeni ürün yoksa event gönderme
    if (totalCount <= previouslySent) return;

    // Sadece yeni eklenen kısmı al
    const newItems = products.slice(previouslySent);
    let globalIndex = startIndex + previouslySent;

    // Yeni ürünleri 8'erli gruplara böl ve her grup için ayrı VIEW_ITEM_LIST event'i gönder
    for (let i = 0; i < newItems.length; i += 8) {
      const group = newItems.slice(i, i + 8);

      trackingDispatch({
        type: TrackingEvent.VIEW_ITEM_LIST,
        payload: {
          productType: ProductType.HOTEL,
          products: group,
          passengers,
          currencyCode,
          item_list_id: itemListId,
          item_list_name: itemListName,
          startIndex: globalIndex,
          cross_product_promotion: crossProductPromotion,
        },
      });

      globalIndex += group.length;
    }

    // Son gönderilen toplam ürün sayısını güncelle
    lastCountRef.current = totalCount;
  }, [
    products,
    passengers,
    currencyCode,
    itemListId,
    itemListName,
    startIndex,
    crossProductPromotion,
    enabled,
    trackingDispatch,
  ]);

  const resetViewedHotels = () => {
    // Yeni bir listeleme (yeni filtre / sayfa) için sayaç sıfırlanabilir
    lastCountRef.current = 0;
  };

  return {
    resetViewedHotels,
  };
}

